import { useCallback, useEffect, useState } from 'react';
import debounce from 'students/utils/native/debounce';

const FALLBACK_ASPECT_RATIO = 720 / 1280;
const MIN_ALLOWED_HEIGHT = 120;

function onError(e: ErrorEvent): boolean | undefined {
  if (e.message.match(/ResizeObserver loop limit exceeded/i)) {
    e.preventDefault();
    // eslint-disable-next-line no-console
    console.warn(e.message);
    return false;
  }

  return undefined;
}

export default function useCheckForResize(): [
  (node: HTMLElement | null) => void,
  () => void
] {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const ref = useCallback((node: HTMLElement | null) => {
    setContainer(node);
  }, []);

  const checkForResize = useCallback(() => {
    if (document.fullscreenElement) return;

    if (container) {
      const cover = container.querySelector('img') || container.querySelector('video');
      const fallbackHeight = Math.round(container.offsetWidth * FALLBACK_ASPECT_RATIO);

      const applyContainerHeight = (height: number): void => {
        if (height >= MIN_ALLOWED_HEIGHT) {
          container.style.height = `${height}px`;
        } else {
          container.style.height = 'auto';
        }
      };

      if (cover) {
        /*
         * Both values must stay relative so every pass measures the *container*, never
         * what a previous pass wrote: writing the measured width back as `<n>px` pinned
         * the element, so the box could only grow and after a rotation into a narrower
         * viewport it stayed wide and was clipped by `overflow: hidden`. And `100%` rather
         * than clearing the properties, because react-player renders its own inline
         * `width/height: 100%` and React will not re-apply an unchanged style prop.
         */
        cover.style.width = '100%';
        cover.style.height = 'auto';

        const measuredHeight = cover.offsetHeight;
        const measuredWidth = cover.offsetWidth;

        if (!measuredHeight || !measuredWidth) {
          applyContainerHeight(fallbackHeight);

          return;
        }

        applyContainerHeight(Math.max(measuredHeight, fallbackHeight));
      } else {
        applyContainerHeight(fallbackHeight);
      }
    }
  }, [container]);

  useEffect(() => {
    window.addEventListener('error', onError);

    return () => {
      window.removeEventListener('error', onError);
    };
  }, []);

  useEffect(() => {
    // Observe the container, not the window: fullscreen exit, a sibling collapsing or the
    // questions block mounting all re-fit the media without a viewport resize.
    if (!container || typeof ResizeObserver === 'undefined') {
      // Older WebViews without ResizeObserver keep the previous behaviour.
      const debouncedCallback = debounce(checkForResize, 500);
      window.addEventListener('resize', debouncedCallback);

      return () => {
        window.removeEventListener('resize', debouncedCallback);
      };
    }

    // Width only: ignoring height keeps the container height written above from feeding
    // back in as another observation.
    let lastWidth = container.offsetWidth;
    const observer = new ResizeObserver(() => {
      const nextWidth = container.offsetWidth;
      if (nextWidth === lastWidth) return;

      lastWidth = nextWidth;
      checkForResize();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [checkForResize, container]);

  return [ref, checkForResize];
}
